import { useEffect, useRef } from 'react';
import { forwardFromYaw, type MapData } from '@etown/shared';
import { WORLD } from '../../lib/palette';
import { useViewStore } from '../../stores/viewStore';
import { useOffice } from './OfficeContext';
import './office.css';

const WIDTH = 176;
const HEIGHT = 132;
/** Wie oft die Minimap neu gezeichnet wird — sie muss nicht 60-mal zappeln. */
const REDRAW_HZ = 8;

function cssColor(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`;
}

/**
 * Kleine Übersichtskarte in der Ecke: zeigt Wände, Räume und wer wo steht.
 * In der Ego-Perspektive sieht man sonst nur den Raum, in dem man gerade ist —
 * die Karte gibt die Orientierung zurück, die die Draufsicht von selbst hat.
 */
export function Minimap({ map }: { map: MapData }) {
  const { runtime } = useOffice();
  const mode = useViewStore((s) => s.mode);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const staticRef = useRef<HTMLCanvasElement | null>(null);

  // Wände und Zonen ändern sich nur bei map:updated — einmal vorzeichnen
  useEffect(() => {
    const layer = document.createElement('canvas');
    layer.width = WIDTH;
    layer.height = HEIGHT;
    const ctx = layer.getContext('2d');
    if (!ctx) return;
    const scale = Math.min(WIDTH / map.width, HEIGHT / map.height);
    const offsetX = (WIDTH - map.width * scale) / 2;
    const offsetY = (HEIGHT - map.height * scale) / 2;

    ctx.fillStyle = cssColor(WORLD.background);
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    ctx.fillStyle = cssColor(WORLD.floorStone);
    ctx.fillRect(offsetX, offsetY, map.width * scale, map.height * scale);

    for (const zone of map.zones) {
      ctx.fillStyle = zone.color;
      ctx.globalAlpha = zone.audio === 'zone' ? 0.5 : 0.28;
      for (const r of zone.rects) {
        ctx.fillRect(offsetX + r.x * scale, offsetY + r.y * scale, r.w * scale, r.h * scale);
      }
    }
    ctx.globalAlpha = 1;

    ctx.fillStyle = cssColor(WORLD.wall);
    map.layers.walls.forEach((row, y) => {
      row.forEach((tile, x) => {
        if (tile) ctx.fillRect(offsetX + x * scale, offsetY + y * scale, Math.ceil(scale), Math.ceil(scale));
      });
    });
    staticRef.current = layer;
  }, [map]);

  // Spieler pro Tick darüberlegen
  useEffect(() => {
    if (mode !== '3d') return;
    let timer = 0;
    const scale = Math.min(WIDTH / map.width, HEIGHT / map.height);
    const offsetX = (WIDTH - map.width * scale) / 2;
    const offsetY = (HEIGHT - map.height * scale) / 2;

    const draw = (): void => {
      const canvas = canvasRef.current;
      const base = staticRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx || !base) return;
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      ctx.drawImage(base, 0, 0);

      ctx.fillStyle = cssColor(WORLD.chair);
      for (const peer of runtime.peerPositions()) {
        ctx.beginPath();
        ctx.arc(offsetX + peer.x * scale, offsetY + peer.y * scale, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      const self = runtime.selfPose();
      const sx = offsetX + self.x * scale;
      const sy = offsetY + self.y * scale;
      if (self.yaw !== null) {
        // Blickkeil, damit man sofort sieht, wohin man schaut
        const f = forwardFromYaw(self.yaw);
        ctx.strokeStyle = cssColor(WORLD.screenGlow);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx + f.x * 11, sy + f.y * 11);
        ctx.stroke();
      }
      ctx.fillStyle = cssColor(WORLD.screenGlow);
      ctx.beginPath();
      ctx.arc(sx, sy, 3.5, 0, Math.PI * 2);
      ctx.fill();
    };

    draw();
    timer = window.setInterval(draw, 1000 / REDRAW_HZ);
    return () => window.clearInterval(timer);
  }, [map, mode, runtime]);

  if (mode !== '3d') return null;
  return (
    <canvas
      ref={canvasRef}
      className="office__minimap"
      width={WIDTH}
      height={HEIGHT}
      aria-label="Übersichtskarte der Etage"
    />
  );
}
